import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import DropDownSearch from "@components/ui/dropdown/DropDownSearch";
import { sample_repertorys } from "@screens/sampleData";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import Button from "@components/ui/buttons/Button";
import Dropzone from "@components/ui/blocks/Dropzone";
import { useEffect, useMemo, useState } from "react";
import {
  SignerSelectionList,
  SelectedSigner,
} from "@components/ui/blocks/SignerSelectionList";
import { Project } from "types";
import {
  PlacementMode,
  InlineFieldsSummary,
  PlaceFieldsOverlay,
} from "@components/modals/signature/PlacementModeControls";
import type { FieldData } from "@components/modals/signature";

const SIGNER_COLORS = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-teal-500",
];

export interface FolderOption {
  value: string;
  label: string;
}

export interface ControlPointOption {
  value: string;
  label: string;
}

export interface DropFileModalSaveData {
  files: File[];
  repertory: string;
  electronicSignature: boolean;
  internalDocument: boolean;
  controlpointId?: string;
  signers?: SelectedSigner[];
  /**
   * When `electronicSignature` is true, the placement mode chosen by the
   * user. Defaults to "appended_page" for backwards compatibility.
   */
  placementMode?: PlacementMode;
  /** Only present when placementMode === "inline_fields": the FIRST file's
   *  fields (kept for callers that handle a single document). */
  inlineFields?: FieldData[];
  inlineDimensions?: { width: number; height: number };
  /**
   * Only present when placementMode === "inline_fields": the fields placed
   * on EACH dropped PDF, keyed by file name. Several PDFs in one drop each
   * get their own placement (one signature request per file).
   */
  inlinePerFile?: InlinePlacementForFile[];
}

export interface InlinePlacementForFile {
  fileName: string;
  fields: FieldData[];
  dimensions: { width: number; height: number };
}

/** Stable key for a dropped File across re-renders. */
const fileKey = (file: File) => `${file.name}::${file.size}::${file.lastModified}`;

interface DropFileModalProps {
  onClose: () => void;
  onSave?: (data: DropFileModalSaveData) => void;
  title?: string;
  iconName?: string;
  saveButtonLabel?: string;
  showRepertorySelector?: boolean;
  showElectronicSignature?: boolean;
  showInternalDocument?: boolean;
  acceptedFileTypes?: string;
  folderOptions?: FolderOption[];
  showImagePreview?: boolean;
  controlpointOptions?: ControlPointOption[];
  project?: Project;
}

export const DropFileModal = ({
  onClose,
  onSave,
  title = "Déposer un document",
  iconName = "file",
  saveButtonLabel = "Enregistrer",
  showRepertorySelector = true,
  showElectronicSignature = true,
  showInternalDocument = true,
  acceptedFileTypes,
  folderOptions,
  showImagePreview = false,
  controlpointOptions,
  project,
}: DropFileModalProps) => {
  const options =
    folderOptions || sample_repertorys.map((r) => ({ value: r, label: r }));

  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [selectedRepertory, setSelectedRepertory] = useState<string>(
    options[0]?.value || "",
  );
  const [electronicSignature, setElectronicSignature] =
    useState<boolean>(false);
  const [internalDocument, setInternalDocument] = useState<boolean>(false);
  const [selectedControlpoint, setSelectedControlpoint] = useState<string>("");
  const [signers, setSigners] = useState<SelectedSigner[]>([]);
  // Fields placed per dropped PDF (keyed by fileKey). Several PDFs in one
  // drop each get their own placement — before, a multi-file drop silently
  // fell back to the appended recap page and the blocks could not be
  // positioned on the documents.
  const [inlineByFile, setInlineByFile] = useState<
    Record<string, { fields: FieldData[]; dimensions: { width: number; height: number } | null }>
  >({});
  const [placeFieldsFor, setPlaceFieldsFor] = useState<string | null>(null);

  const signerPickerVisible =
    showElectronicSignature && !!project && electronicSignature;

  // Inline mode needs PDFs to render before upload. We create a local blob
  // URL per dropped PDF so PrepareSignature has something to render. The
  // URLs are revoked when the file list changes or the modal unmounts so we
  // don't leak memory.
  const firstFile = droppedFiles[0];
  const inlineCandidate =
    droppedFiles.length > 0 &&
    droppedFiles.every((f) => f.type === "application/pdf");
  const inlinePdfUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    if (inlineCandidate) {
      for (const f of droppedFiles) urls[fileKey(f)] = URL.createObjectURL(f);
    }
    return urls;
  }, [inlineCandidate, droppedFiles]);
  useEffect(() => {
    return () => {
      for (const url of Object.values(inlinePdfUrls)) URL.revokeObjectURL(url);
    };
  }, [inlinePdfUrls]);

  // If the user toggles signature off, or drops non-PDF files, drop any
  // placed fields back to the empty state so the summary matches; when a
  // file is removed from the drop, forget its placement.
  useEffect(() => {
    if (!signerPickerVisible || !inlineCandidate) {
      setInlineByFile({});
      return;
    }
    const keys = new Set(droppedFiles.map(fileKey));
    setInlineByFile((prev) => {
      const next: typeof prev = {};
      for (const [k, v] of Object.entries(prev)) if (keys.has(k)) next[k] = v;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [signerPickerVisible, inlineCandidate, droppedFiles]);

  const placementFor = (file: File) =>
    inlineByFile[fileKey(file)] ?? { fields: [], dimensions: null };
  // First file's placement: what single-document callers still read.
  const inlineFields = firstFile ? placementFor(firstFile).fields : [];
  const inlineDimensions = firstFile ? placementFor(firstFile).dimensions : null;

  const inlineModeDisabled = !signerPickerVisible || !inlineCandidate;

  // The recap-page ("appended_page") mode is no longer user-selectable in the
  // web signature flow — it's kept only for compte-rendu generation and the
  // mobile app. Web places inline fields whenever the dropped file is a single
  // PDF, and silently falls back to appended_page for non-PDF documents (where
  // fields can't be placed).
  const placementMode: PlacementMode = inlineModeDisabled
    ? "appended_page"
    : "inline_fields";

  // Inline mode is invalid if, on ANY dropped PDF:
  //   - no fields placed / no dimensions,
  //   - or some selected signers don't have a field yet.
  const signersWithoutField = (file: File) => {
    const ids = new Set(
      placementFor(file).fields.map((f) => f.signer_id).filter(Boolean) as string[],
    );
    return signers.filter((s) => !ids.has(s.id));
  };
  const fileInlineInvalid = (file: File) => {
    const { fields, dimensions } = placementFor(file);
    return fields.length === 0 || !dimensions || signersWithoutField(file).length > 0;
  };
  const inlineInvalid =
    placementMode === "inline_fields" &&
    signerPickerVisible &&
    droppedFiles.some(fileInlineInvalid);

  const saveDisabled =
    droppedFiles.length === 0 ||
    (signerPickerVisible && signers.length === 0) ||
    inlineInvalid;

  const handleSave = () => {
    if (droppedFiles.length === 0) {
      alert("Veuillez sélectionner au moins un fichier");
      return;
    }
    if (signerPickerVisible && signers.length === 0) {
      alert("Sélectionnez au moins un signataire");
      return;
    }
    if (
      placementMode === "inline_fields" &&
      signerPickerVisible
    ) {
      for (const file of droppedFiles) {
        const { fields, dimensions } = placementFor(file);
        const label = droppedFiles.length > 1 ? ` sur « ${file.name} »` : "";
        if (fields.length === 0 || !dimensions) {
          alert(`Placez au moins un champ${label}.`);
          return;
        }
        const missing = signersWithoutField(file);
        if (missing.length > 0) {
          alert(
            `Signataire(s) sans champ${label} : ${missing.map((m) => m.signer_name).join(", ")}`,
          );
          return;
        }
      }
    }

    if (onSave) {
      const effectiveMode: PlacementMode | undefined = signerPickerVisible
        ? placementMode
        : undefined;
      const inlinePerFile: InlinePlacementForFile[] | undefined =
        effectiveMode === "inline_fields"
          ? droppedFiles.flatMap((file) => {
              const { fields, dimensions } = placementFor(file);
              return dimensions ? [{ fileName: file.name, fields, dimensions }] : [];
            })
          : undefined;
      onSave({
        files: droppedFiles,
        repertory: selectedRepertory,
        electronicSignature,
        internalDocument,
        controlpointId: selectedControlpoint || undefined,
        signers: signerPickerVisible ? signers : undefined,
        placementMode: effectiveMode,
        inlineFields:
          effectiveMode === "inline_fields" ? inlineFields : undefined,
        inlineDimensions:
          effectiveMode === "inline_fields" && inlineDimensions
            ? inlineDimensions
            : undefined,
        inlinePerFile,
      });
    }
  };

  return (
    <BaseModal
      title={title}
      startIcon={<Icon1 name={iconName} size={20}></Icon1>}
      onClose={onClose}
    >
      <Dropzone
        onDrop={(files) => {
          setDroppedFiles(files);
        }}
        files={droppedFiles}
        setSelectedDroppedFiles={setDroppedFiles}
        onDelete={(file) =>
          setDroppedFiles(droppedFiles.filter((f) => f !== file))
        }
        showImagePreview={showImagePreview}
        acceptTypes={
          acceptedFileTypes === "image/*"
            ? {
                "image/jpeg": [".jpg", ".jpeg"],
                "image/png": [".png"],
                "image/webp": [".webp"],
              }
            : undefined
        }
      ></Dropzone>
      <div className="bg-white flex flex-col gap-2 p-4 border-t-1">
        {showRepertorySelector && (
          <DropDown
            options={options}
            label="Choisir un répertoire"
            value={selectedRepertory}
            onSelect={(value) => {
              setSelectedRepertory(value as string);
            }}
          ></DropDown>
        )}

        {showElectronicSignature && (
          <SwitchBrick
            title="Signature électronique"
            description="Envoyer ce document pour signature"
            checked={electronicSignature}
            onChange={(newState) => {
              setElectronicSignature(newState);
            }}
          />
        )}

        {showInternalDocument && (
          <SwitchBrick
            title="Document interne"
            description="Ne pas partager ce document avec le client"
            checked={internalDocument}
            onChange={(newState) => {
              setInternalDocument(newState);
            }}
          ></SwitchBrick>
        )}

        {controlpointOptions && controlpointOptions.length > 0 && (
          <DropDownSearch
            options={[{ value: "", label: "Aucun" }, ...controlpointOptions]}
            label="Associer à un point de contrôle"
            placeholder="Rechercher un point de contrôle..."
            value={selectedControlpoint}
            onSelect={(value) => {
              setSelectedControlpoint(value as string);
            }}
          />
        )}

        {signerPickerVisible && project && (
          <div className="mt-2 pt-3 border-t border-neutral-200 flex flex-col gap-3">
            <div>
              <div className="text-h3 font-semibold mb-1">
                Qui doit signer ce{droppedFiles.length > 1 ? "s" : ""} document
                {droppedFiles.length > 1 ? "s" : ""} ?
              </div>
              {droppedFiles.length > 1 && (
                <div className="text-h4 text-neutral-400 mb-3">
                  Les mêmes signataires seront appliqués aux{" "}
                  {droppedFiles.length} documents, avec des champs placés sur
                  chacun.
                </div>
              )}
              <SignerSelectionList
                project={project}
                signers={signers}
                setSigners={setSigners}
              />
            </div>

            {placementMode === "inline_fields" &&
              droppedFiles.map((file) => {
                const key = fileKey(file);
                const { fields } = placementFor(file);
                return (
                  <div key={key} className="flex flex-col gap-1">
                    {droppedFiles.length > 1 && (
                      <div className="text-h4 font-medium text-neutral-700 truncate">
                        {file.name}
                      </div>
                    )}
                    <InlineFieldsSummary
                      count={fields.length}
                      disabled={!inlinePdfUrls[key] || signers.length === 0}
                      perSignerCounts={signers.map((s, i) => ({
                        id: s.id,
                        name: s.signer_name || `Signataire ${i + 1}`,
                        count: fields.filter((f) => f.signer_id === s.id).length,
                      }))}
                      onEdit={() => setPlaceFieldsFor(key)}
                      onClear={() =>
                        setInlineByFile((prev) => {
                          const next = { ...prev };
                          delete next[key];
                          return next;
                        })
                      }
                    />
                  </div>
                );
              })}
          </div>
        )}
      </div>
      <PlaceFieldsOverlay
        open={placeFieldsFor !== null}
        pdfUrl={placeFieldsFor ? inlinePdfUrls[placeFieldsFor] ?? null : null}
        documentName={
          droppedFiles.find((f) => fileKey(f) === placeFieldsFor)?.name ?? "Document"
        }
        initialFields={placeFieldsFor ? inlineByFile[placeFieldsFor]?.fields ?? [] : []}
        initialDimensions={
          placeFieldsFor ? inlineByFile[placeFieldsFor]?.dimensions ?? null : null
        }
        signers={signers.map((s, i) => ({
          id: s.id,
          name: s.signer_name || `Signataire ${i + 1}`,
          color: SIGNER_COLORS[i % SIGNER_COLORS.length],
        }))}
        onClose={() => setPlaceFieldsFor(null)}
        onConfirm={(fields, dimensions) => {
          if (placeFieldsFor) {
            const key = placeFieldsFor;
            setInlineByFile((prev) => ({ ...prev, [key]: { fields, dimensions } }));
          }
          setPlaceFieldsFor(null);
        }}
      />
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={saveButtonLabel}
          onClick={handleSave}
          disabled={saveDisabled}
        ></Button>
      </div>
    </BaseModal>
  );
};
